/**
 * 10th of the installment month (local timezone).
 * @param {Date|string} month
 * @returns {Date|null}
 */
export function getQuarterDueDate(month, dueDay = 10) {
  if (!month) return null;
  const d = new Date(month);
  return new Date(d.getFullYear(), d.getMonth(), dueDay);
}

/**
 * First calendar day on which penalty applies (due + penalty_day).
 * @param {{ month?: Date|string, penalty_day?: number }} quarter
 * @returns {Date|null}
 */
export function getPenaltyDeadlineDate(quarter) {
  const due = getQuarterDueDate(quarter?.month);
  if (!due) return null;
  const deadline = new Date(due);
  deadline.setDate(deadline.getDate() + Number(quarter?.penalty_day || 0));
  return deadline;
}

/**
 * @param {{ month?: Date|string, penalty_day?: number }} quarter
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isPenaltyApplicable(quarter, now = new Date()) {
  const deadline = getPenaltyDeadlineDate(quarter);
  if (!deadline) return false;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const deadlineDay = new Date(
    deadline.getFullYear(),
    deadline.getMonth(),
    deadline.getDate()
  );
  return today >= deadlineDay;
}

export function formatQuarterDate(date) {
  if (!date) return '';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

/**
 * Parse quarter month from API Date or HTML month input (YYYY-MM).
 * @param {Date|string} month
 * @returns {Date|null}
 */
export function parseQuarterMonth(month) {
  if (!month) return null;
  if (typeof month === 'string' && /^\d{4}-\d{2}$/.test(month)) {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1, 1);
  }
  const d = new Date(month);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * 10th of installment month, formatted for display.
 * @param {Date|string} month
 * @returns {string|null}
 */
export function formatEffectiveDueDate(month, dueDay = 10) {
  const parsed = parseQuarterMonth(month);
  if (!parsed) return null;
  return formatQuarterDate(getQuarterDueDate(parsed, dueDay));
}

/**
 * Date when pre-due reminder is sent (due date minus alert_day), per fee cron.
 * @param {Date|string} month
 * @param {number|string} alertDay
 * @returns {string|null}
 */
export function formatAlertSendDate(month, alertDay, dueDay = 10) {
  const parsed = parseQuarterMonth(month);
  const days = Number(alertDay);
  if (!parsed || !days || days < 1) return null;
  const due = getQuarterDueDate(parsed, dueDay);
  const alertDate = new Date(due);
  alertDate.setDate(alertDate.getDate() - days);
  return formatQuarterDate(alertDate);
}

/**
 * First day penalty applies (due date + penalty_day grace days).
 * @param {Date|string} month
 * @param {number|string} penaltyDay
 * @returns {string|null}
 */
export function formatPenaltyApplyDate(month, penaltyDay) {
  const days = Number(penaltyDay);
  if (!month || Number.isNaN(days) || days < 1) return null;
  const deadline = getPenaltyDeadlineDate({ month, penalty_day: penaltyDay });
  if (!deadline) return null;
  return formatQuarterDate(deadline);
}

/**
 * Penalty amount when late fee applies: base_fee × (penalty_percentage / 100).
 * @param {number|string} baseFee
 * @param {number|string} penaltyPercentage
 * @returns {number|null}
 */
export function calculatePenaltyAmount(baseFee, penaltyPercentage) {
  const fee = Number(baseFee);
  const pct = Number(penaltyPercentage);
  if (Number.isNaN(fee) || fee < 0 || Number.isNaN(pct) || pct < 0) return null;
  return fee * (pct / 100);
}

/**
 * @param {number|string} baseFee
 * @param {number|string} penaltyPercentage
 * @returns {string|null}
 */
export function formatPenaltyAmount(baseFee, penaltyPercentage) {
  const amount = calculatePenaltyAmount(baseFee, penaltyPercentage);
  if (amount === null) return null;
  return amount.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  });
}
